import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';

/**
 * A custom element that displays a cart title with auto-updating cart count.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} cartBubble - The cart bubble element.
 * @property {HTMLElement} cartBubbleText - The cart bubble text element.
 * @property {HTMLElement} cartBubbleCount - The cart bubble count element.
 * @property {HTMLElement} cartTitle - The cart title element.
 *
 * @extends {Component<Refs>}
 */
class CartTitle extends Component {
  requiredRefs = ['cartBubble', 'cartBubbleText', 'cartBubbleCount', 'cartTitle'];

  connectedCallback() {
    super.connectedCallback();
    
    // Listen for cart update events
    document.addEventListener(ThemeEvents.cartUpdate, this.onCartUpdate);
    
    // Initialize with current cart count after a short delay to ensure DOM is ready
    setTimeout(() => {
      this.updateCartCount();
    }, 100);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    
    // Remove event listener
    document.removeEventListener(ThemeEvents.cartUpdate, this.onCartUpdate);
  }

  /**
   * Handles cart update events
   * @param {CustomEvent} event - The cart update event
   */
  onCartUpdate = (event) => {
    const itemCount = event.detail.data?.itemCount ?? 0;
    this.updateCartCount(itemCount);
  };

  /**
   * Updates the cart count display
   * @param {number} [itemCount] - The number of items in the cart. If not provided, fetches from cart data.
   */
  updateCartCount(itemCount) {
    if (itemCount === undefined) {
      // Try to get cart count from various sources
      itemCount = this.getCurrentCartCount();
    }

    // Debug logging
    console.log('CartTitle: Updating cart count to:', itemCount);
    console.log('CartTitle: Title text:', this.dataset.title);
    console.log('CartTitle: Show count:', this.dataset.showCount);

    const titleText = this.dataset.title || 'Cart';
    const showCount = this.dataset.showCount === 'true';
    
    // Update the title element
    if (this.refs.cartTitle) {
      const titleElement = this.refs.cartTitle;
      
      if (itemCount === 0) {
        // Show empty cart message
        titleElement.innerHTML = 'Your cart is empty';
      } else {
        // Show normal title
        if (showCount) {
          // Create or update cart bubble
          let cartBubble = titleElement.querySelector('.cart-bubble');
          if (!cartBubble) {
            cartBubble = document.createElement('span');
            cartBubble.className = 'cart-bubble';
            cartBubble.innerHTML = `
              <span class="cart-bubble__background"></span>
              <span class="cart-bubble__text">
                <span class="visually-hidden">Cart count: ${itemCount}</span>
                <span class="cart-bubble__text-count" aria-hidden="true">${itemCount > 99 ? '99+' : itemCount}</span>
              </span>
            `;
          } else {
            // Update existing cart bubble
            const countElement = cartBubble.querySelector('.cart-bubble__text-count');
            const hiddenElement = cartBubble.querySelector('.visually-hidden');
            if (countElement) countElement.textContent = itemCount > 99 ? '99+' : itemCount;
            if (hiddenElement) hiddenElement.textContent = `Cart count: ${itemCount}`;
            
            // Add animation class for updates
            cartBubble.classList.add('cart-bubble--updating');
            setTimeout(() => {
              cartBubble.classList.remove('cart-bubble--updating');
            }, 300);
          }
          
          titleElement.innerHTML = `${titleText} `;
          titleElement.appendChild(cartBubble);
        } else {
          titleElement.textContent = titleText;
        }
      }
    }
  }

  /**
   * Gets the current cart count from various sources
   * @returns {number} The current cart item count
   */
  getCurrentCartCount() {
    // Try to get from the initial cart count data attribute first
    if (this.refs.cartTitle) {
      const initialCount = this.refs.cartTitle.dataset.initialCartCount;
      console.log('CartTitle: Initial cart count from data attribute:', initialCount);
      if (initialCount !== undefined) {
        const count = parseInt(initialCount, 10);
        if (!isNaN(count)) {
          console.log('CartTitle: Using initial cart count:', count);
          return count;
        }
      }
    }
    
    // Try to get from window.Shopify.cart
    if (window.Shopify?.cart?.item_count !== undefined) {
      return window.Shopify.cart.item_count;
    }
    
    // Try to get from sessionStorage
    try {
      const cartData = sessionStorage.getItem('cart-count');
      if (cartData) {
        const parsed = JSON.parse(cartData);
        return parseInt(parsed.value, 10) || 0;
      }
    } catch (e) {
      // Ignore sessionStorage errors
    }
    
    // Try to get from existing cart bubble on page
    const existingCartBubble = document.querySelector('.cart-bubble__text-count');
    if (existingCartBubble) {
      const countText = existingCartBubble.textContent;
      const count = parseInt(countText, 10);
      if (!isNaN(count)) {
        return count;
      }
    }
    
    // Try to get from cart items component
    const cartItemCount = document.querySelector('[ref="cartItemCount"]');
    if (cartItemCount) {
      const count = parseInt(cartItemCount.textContent, 10);
      if (!isNaN(count)) {
        return count;
      }
    }
    
    // Try to detect if cart is empty by looking for empty cart message
    const emptyCartMessage = document.querySelector('.cart-page--empty');
    if (emptyCartMessage) {
      console.log('CartTitle: Detected empty cart from CSS class');
      return 0;
    }
    
    // Try to detect if cart has items by looking for cart items
    const cartItems = document.querySelectorAll('.cart-item, [data-cart-item]');
    if (cartItems.length > 0) {
      console.log('CartTitle: Detected cart items:', cartItems.length);
      return cartItems.length;
    }
    
    console.log('CartTitle: No cart count found, defaulting to 0');
    // Default to 0 if nothing found
    return 0;
  }

  /**
   * Updates the cart title settings
   * @param {string} title - The cart title text
   * @param {boolean} showCount - Whether to show the cart count
   */
  updateSettings(title, showCount) {
    this.dataset.title = title;
    this.dataset.showCount = showCount.toString();
    this.updateCartCount();
  }
}

// Register the custom element
customElements.define('cart-title', CartTitle);
